import { MultipleChoiceExamStatics, MultipleChoiceQuestionDetail } from '../../../../../store/workspace/types';
import { ListItem } from '../../../../../store/general/types';
import { ChevronDownIcon, EmptyStateIcon } from '../../../../../assets/icons';
import DropDown from '../../../../../components/DropDown';
import { CodeBlock, dracula } from 'react-code-blocks';
import styles from './style.module.scss';
import { useEffect, useState } from 'react';

interface Question extends MultipleChoiceQuestionDetail {
    index: number;
};

const questionTypeFilters = [
    {
        id: 'all',
        title: 'All'
    },
    {
        id: 'technical',
        title: 'Technical'
    },
    {
        id: 'general',
        title: 'General'
    },
    {
        id: 'coding',
        title: 'Coding'
    },
];

const questionLevelFilters = [
    {
        id: 'all',
        title: 'All'
    },
    {
        id: 'easy',
        title: 'Easy'
    },
    {
        id: 'medium',
        title: 'Medium'
    },
    {
        id: 'hard',
        title: 'Hard'
    },
];

interface Props {
    examData: MultipleChoiceExamStatics | null;
};

export default function MultipleChoiceExamSheet(props: Props) {
    const { examData } = props;
    const [questions, setQuestions] = useState<Question[]>([]);
    const [questionType, setQuestionType] = useState<ListItem>(questionTypeFilters[0]);
    const [questionLevel, setQuestionLevel] = useState<ListItem>(questionLevelFilters[0]);
    const [expandedCards, setExpandedCards] = useState<number[]>([]);

    const toggleCard = (index: number) => {
        setExpandedCards(prev => {
            if (prev.includes(index)) {
                return prev.filter(i => i !== index);
            } else {
                return [...prev, index];
            }
        });
    };

    useEffect(() => {
        filter();
    }, [examData, questionType, questionLevel]);

    const filter = () => {
        if (!examData) return;
        let data: Question[] = [];

        if (questionType.id === 'all' && questionLevel.id === 'all') {
            examData.exam.questions.forEach((i, index) => {
                data.push({ ...i, index });
            });
        }

        if (questionType.id !== 'all' && questionLevel.id === 'all') {
            examData.exam.questions.forEach((i, index) => {
                if (i.type === questionType.id) {
                    data.push({ ...i, index });
                }
            });
        }

        if (questionType.id === 'all' && questionLevel.id !== 'all') {
            examData.exam.questions.forEach((i, index) => {
                if (i.level === questionLevel.id) {
                    data.push({ ...i, index });
                }
            });
        }

        if (questionType.id !== 'all' && questionLevel.id !== 'all') {
            examData.exam.questions.forEach((i, index) => {
                if (i.level === questionLevel.id && i.type === questionType.id) {
                    data.push({ ...i, index });
                }
            });
        }

        setQuestions(data);
    };

    return (
        <div className={styles.wrapper}>
            {examData ? <>
                <div className={styles.header}>
                    <div className={styles.header_top}>
                        <span className={styles.header_top_title}>
                            {examData.exam?.jobTitle}
                        </span>
                        <div className={styles.header_top_actions}>
                            <DropDown
                                idKey='id'
                                titleKey='title'
                                label='Questions level'
                                setItem={setQuestionLevel}
                                selectedItem={questionLevel}
                                dropDownList={questionLevelFilters}
                                containerClassName={styles.header_top_actions_dropdown}
                            />
                            <DropDown
                                idKey='id'
                                titleKey='title'
                                label='Questions type'
                                setItem={setQuestionType}
                                selectedItem={questionType}
                                dropDownList={questionTypeFilters}
                                containerClassName={styles.header_top_actions_dropdown}
                            />
                        </div>
                    </div>
                    <div className={styles.header_bottom}>
                        <span className={styles.header_bottom_item}>
                            Question Quantities : {examData.exam?.n_questions}
                        </span>
                        <span className={styles.header_bottom_item}>
                            Exam Time : {examData.exam?.time}'
                        </span>
                    </div>
                </div>
                <div className={styles.table}>
                    <table>
                        <thead>
                            <th>Questions & Answers</th>
                            <th>Level</th>
                            <th>Type</th>
                        </thead>
                        <tbody>
                            {questions.map(({ choices, answer, question, level, type }, ind) =>
                                <tr>
                                    <td>
                                        <span className={styles.question}>{ind + 1} - {question.split('```')[0].split(/(\`.*?\`)/g).map(i => {
                                            if (i.startsWith('`')) {
                                                return <code className={styles.code}>{i.slice(1, i.length - 1)}</code>
                                            } else {
                                                return <span>{i}</span>
                                            }
                                        })}</span>
                                        <div className={styles.codeBlock}>
                                            {question.includes('```') && <CodeBlock
                                                text={question.split('```')[1]}
                                                language={'python'}
                                                showLineNumbers={true}
                                                theme={dracula}
                                            />}
                                        </div>
                                        <span className={styles.question}>{question.split('```')[2]?.split(/(\`.*?\`)/g).map(i => {
                                            if (i.startsWith('`')) {
                                                return <code className={styles.code}>{i.slice(1, i.length - 1)}</code>
                                            } else {
                                                return <span>{i}</span>
                                            }
                                        })}</span>
                                        <div className={styles.options}>
                                            {choices.map((choice, indx) => <div className={styles.option}>
                                                <span
                                                    className={styles.option_index}
                                                    data-correct-answer={answer === indx}
                                                >
                                                    {indx + 1}
                                                </span> - &nbsp;
                                                {choice.includes('```') ?
                                                    <div className={styles.code_block}>
                                                        <CodeBlock
                                                            text={choice.split('```')[1]}
                                                            language={'python'}
                                                            showLineNumbers={true}
                                                            theme={dracula}
                                                        />
                                                    </div>
                                                    : choice.split(/(\`.*?\`)/g).map(i => {
                                                        if (i.startsWith('`')) {
                                                            return <code className={styles.code}>{i.slice(1, i.length - 1)}</code>
                                                        } else {
                                                            return <span>{i}</span>
                                                        }
                                                    })}
                                            </div>)}
                                        </div>
                                    </td>
                                    <td style={{ textTransform: 'capitalize' }}>{level}</td>
                                    <td style={{ textTransform: 'capitalize' }}>{type}</td>
                                </tr>)}
                        </tbody>
                    </table>
                </div>

                <div className={styles.mobile_list}>
                    {questions.map(({ choices, answer, question, level, type }, ind) => {
                        const isExpanded = expandedCards.includes(ind);
                        return (
                            <div className={styles.card} onClick={() => toggleCard(ind)}>
                                <div className={styles.card_header}>
                                    <span>Question {ind + 1}</span>
                                    <ChevronDownIcon style={{ transform: isExpanded ? 'rotate(180deg)' : 'none' }} />
                                </div>
                                {isExpanded && <div className={styles.card_body}>
                                    <div className={styles.card_body_section}>
                                        <div className={styles.card_body_section_content}>
                                            <span className={styles.question}>
                                                {question.split('```')[0].split(/(\`.*?\`)/g).map(i => {
                                                    if (i.startsWith('`')) {
                                                        return <code className={styles.code}>{i.slice(1, i.length - 1)}</code>
                                                    } else {
                                                        return <span>{i}</span>
                                                    }
                                                })}
                                            </span>
                                            {question.includes('```') && <div className={styles.codeBlock}>
                                                <CodeBlock
                                                    text={question.split('```')[1]}
                                                    language={'python'}
                                                    showLineNumbers={true}
                                                    theme={dracula}
                                                />
                                            </div>}
                                            <span className={styles.question}>{question.split('```')[2]?.split(/(\`.*?\`)/g).map(i => {
                                                if (i.startsWith('`')) {
                                                    return <code className={styles.code}>{i.slice(1, i.length - 1)}</code>
                                                } else {
                                                    return <span>{i}</span>
                                                }
                                            })}</span>
                                            <div className={styles.options}>
                                                {choices.map((choice, indx) => <div className={styles.option}>
                                                    <span
                                                        className={styles.option_index}
                                                        data-correct-answer={answer === indx}
                                                    >
                                                        {indx + 1}
                                                    </span> - &nbsp;
                                                    {choice.includes('```') ?
                                                        <div className={styles.code_block}>
                                                            <CodeBlock
                                                                text={choice.split('```')[1]}
                                                                language={'python'}
                                                                showLineNumbers={true}
                                                                theme={dracula}
                                                            />
                                                        </div>
                                                        : choice.split(/(\`.*?\`)/g).map(i => {
                                                            if (i.startsWith('`')) {
                                                                return <code className={styles.code}>{i.slice(1, i.length - 1)}</code>
                                                            } else {
                                                                return <span>{i}</span>
                                                            }
                                                        })}
                                                </div>)}
                                            </div>

                                        </div>
                                    </div>
                                    <div className={styles.card_body_section}>
                                        <div className={styles.card_body_section_title}>
                                            <span>Level</span>
                                            <span>{level}</span>
                                        </div>
                                    </div>
                                    <div className={styles.card_body_section}>
                                        <div className={styles.card_body_section_title}>
                                            <span>Type</span>
                                            <span>{type}</span>
                                        </div>
                                    </div>
                                </div>}
                            </div>)
                    })}
                </div>
            </> : <div className={styles.empty_state}>
                <EmptyStateIcon />
                <span>No data.</span>
            </div>}
        </div>
    )
};
